// Plugs this app's API into the shared content screens before any route renders.
import "./lib/content-host.ts";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { ROUTE_PENDING_MIN_MS, ROUTE_PENDING_MS, ThemeProvider } from "@bs/ui";
import { routeTree } from "./routeTree.gen.ts";
import { isUnauthorized } from "./lib/auth.ts";
import "./index.css";

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE || "development",
  });
}

const queryClient = new QueryClient({
  // A 401 anywhere means the session ended: go to the sign-in page instead of showing broken screens.
  queryCache: new QueryCache({
    onError: (err) => {
      if (isUnauthorized(err) && window.location.pathname !== "/login") window.location.assign("/login");
    },
  }),
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
});

// PLAN §12: pendingMs 150 / pendingMinMs 300; loaders prefetch on link hover.
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  defaultPendingMs: ROUTE_PENDING_MS,
  defaultPendingMinMs: ROUTE_PENDING_MIN_MS,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const root = document.getElementById("root");
if (!root) throw new Error("#root missing");
createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <RouterProvider router={router} />
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
